import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight, ShieldCheck, Truck, Clock, MapPin, Car, Anchor,
  Gem, Warehouse, Star, BadgeCheck, PoundSterling,
} from "lucide-react";
import Seo from "@/components/Seo";
import { FadeUp, MaskedLine, CountUp, Marquee } from "@/components/Motion";

const HERO_IMG = "https://images.unsplash.com/photo-1761917904658-2a9ecb84a169?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ6MzR8MHwxfHNlYXJjaHxfHxjYXIlMjB0cmFuc3BvcnRlciUyMHRydWNrJTIwdHJhbnNwb3J0JTIwY2Fyc3xlbnwwfHx8fDE3OTA1OTk4NzF8MA&ixlib=rb-4.1.0&q=85";
const MACHINERY_IMG = "https://images.unsplash.com/photo-1597399069243-e782acdca4d6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHxoZWF2eSUyMGVxdWlwbWVudCUyMG1hY2hpbmVyeSUyMHRyYW5zcG9ydCUyMGxvcnJ5JTIwdHJhaWxlcnxlbnwwfHx8fDE3OTA1OTgzODJ8MA&ixlib=rb-4.1.0&q=85";
const TRAILER_IMG = "https://images.unsplash.com/photo-1756888218811-76f80423861b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwzfHxoZWF2eSUyMGVxdWlwbWVudCUyMG1hY2hpbmVyeSUyMHRyYW5zcG9ydCUyMGxvcnJ5JTIwdHJhaWxlcnxlbnwwfHx8fDE3OTA1OTgzODJ8MA&ixlib=rb-4.1.0&q=85";
const FLEET_IMG = "https://images.unsplash.com/photo-1640296278310-ac562e1eb272?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHw0fHxoZWF2eSUyMGVxdWlwbWVudCUyMG1hY2hpbmVyeSUyMHRyYW5zcG9ydCUyMGxvcnJ5JTIwdHJhaWxlcnxlbnwwfHx8fDE3OTA1OTgzODJ8MA&ixlib=rb-4.1.0&q=85";

const TRUST_BADGES = [
  { icon: ShieldCheck, label: "FCA-Regulated Payment Protection" },
  { icon: BadgeCheck, label: "Goods in Transit Cover" },
  { icon: Clock, label: "20+ Years' Experience" },
  { icon: MapPin, label: "UK-Wide Coverage" },
];

const STATS = [
  { value: 1580, suffix: "+", label: "Satisfied Clients" },
  { value: 1650, suffix: "+", label: "Vehicles Delivered" },
  { value: 99.8, suffix: "%", decimals: 1, label: "On-Time Completion" },
  { value: 20, suffix: "+", label: "Years' Experience" },
];

const SERVICES = [
  {
    icon: Car, title: "Vehicle Transport", img: FLEET_IMG, span: true,
    copy: "Cars, vans and motorcycles moved door to door on our modern transporters — single vehicles or full loads, anywhere in the UK.",
    tags: ["Cars & vans", "Motorcycles", "Multi-vehicle loads"],
  },
  {
    icon: Warehouse, title: "Plant & Machinery", img: MACHINERY_IMG,
    copy: "Low-loader transport for diggers, telehandlers, access platforms and heavy equipment, with experienced loading crews.",
    tags: ["Low loaders", "Heavy equipment"],
  },
  {
    icon: Anchor, title: "Caravan & Boat Transport",
    copy: "Caravans, motorhomes and boats towed or trailered safely between homes, marinas, storage sites and dealerships.",
    tags: ["Caravans", "Boats & trailers"],
  },
  {
    icon: Gem, title: "Enclosed Prestige Transport",
    copy: "Classic, prestige and high-value vehicles moved in enclosed covered transport — discreet, protected and fully tracked.",
    tags: ["Classics", "Covered & discreet"],
  },
  {
    icon: Truck, title: "Commercial Fleet Movements", img: TRAILER_IMG,
    copy: "Scheduled fleet relocations, dealer transfers and auction collections for businesses that need vehicles moved at scale.",
    tags: ["Dealer transfers", "Auction runs"],
  },
];

const STEPS = [
  { n: "01", title: "Request a customised quote", copy: "Tell us what you're moving and the collection and delivery postcodes. We come back with a fixed, no-obligation price — usually within one working day." },
  { n: "02", title: "We schedule & inspect", copy: "We agree a collection window that suits you. On the day, your vehicle is photographed, condition-checked and loaded by our own trained crew." },
  { n: "03", title: "Safe UK-wide delivery", copy: "Your vehicle travels on our lorries to its destination. You confirm everything is perfect — and only then is your protected payment released." },
];

const TESTIMONIALS = [
  { quote: "Solaris moved three of our pool vans from Portsmouth to Glasgow without a single hiccup. The payment protection made the whole thing feel completely safe.", name: "Sarah P.", role: "Fleet Manager, Portsmouth" },
  { quote: "My E-Type went in an enclosed trailer and arrived concours-ready. Photographs at collection, constant updates — a properly professional outfit.", name: "David W.", role: "Classic Car Owner, Winchester" },
  { quote: "We use them for all our plant movements now. Diggers collected on time, every time, and the lads loading know exactly what they're doing.", name: "Rachel T.", role: "Hire Desk, Birmingham" },
];

const CITIES = ["TADCASTER", "LONDON", "YORK", "BIRMINGHAM", "MANCHESTER", "BRISTOL", "LEEDS", "GLASGOW", "CARDIFF", "NEWCASTLE", "LIVERPOOL", "EDINBURGH"];

const Home = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);

  return (
    <div data-testid="home-page">
      <Seo
        title="Vehicle & Equipment Transport UK | Solaris Transport LTD"
        description="Solaris Transport LTD moves cars, plant machinery, caravans and boats safely across the UK from Tadcaster. FCA-regulated payment protection on every job."
        path="/"
      />

      {/* ——— Kinetic hero ——— */}
      <section ref={heroRef} data-testid="hero-section" className="relative overflow-hidden bg-white">
        <div className="pointer-events-none absolute -top-32 -right-32 h-96 w-96 rounded-full bg-blue-100/60 blur-3xl" />
        <div className="mx-auto grid max-w-7xl gap-12 px-5 pt-16 pb-20 lg:grid-cols-12 lg:items-center lg:gap-8 lg:px-8 lg:pt-24 lg:pb-28">
          <div className="lg:col-span-7">
            <h1 className="font-display text-4xl font-extrabold leading-[1.08] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl" data-testid="hero-headline">
              <MaskedLine delay={0.1}>Vehicles,</MaskedLine>
              <MaskedLine delay={0.22}>moved properly,</MaskedLine>
              <MaskedLine delay={0.34}>
                <span className="text-blue-700">UK-wide.</span>
              </MaskedLine>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.55 }}
              className="mt-6 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg"
              data-testid="hero-subcopy"
            >
              From Tadcaster to anywhere in the UK — cars, plant machinery,
              caravans and boats collected, transported and delivered by our own
              crews, with FCA-regulated payment protection as standard.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.7 }}
              className="mt-8 flex flex-wrap items-center gap-4"
            >
              <Link
                to="/contact"
                data-testid="hero-get-quote-button"
                className="group flex items-center gap-2 rounded-full bg-blue-700 px-7 py-3.5 text-base font-bold text-white shadow-lg shadow-blue-700/25 transition-all duration-200 hover:bg-blue-800 hover:shadow-blue-800/30"
              >
                Get a free quote
                <ArrowRight className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/payment-protection"
                data-testid="hero-payment-link"
                className="flex items-center gap-2 rounded-full border border-slate-300 bg-white px-7 py-3.5 text-base font-bold text-slate-800 transition-colors duration-200 hover:border-blue-400 hover:text-blue-700"
              >
                <ShieldCheck className="h-5 w-5 text-blue-700" />
                How payment protection works
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.9 }}
              className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-4"
              data-testid="hero-trust-badges"
            >
              {TRUST_BADGES.map((b) => (
                <div key={b.label} className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2.5">
                  <b.icon className="h-4 w-4 shrink-0 text-blue-700" />
                  <span className="text-xs font-semibold leading-tight text-slate-700">{b.label}</span>
                </div>
              ))}
            </motion.div>
          </div>

          <div className="relative lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 24 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
            >
              <div className="overflow-hidden rounded-3xl border border-slate-200 shadow-2xl shadow-blue-900/10">
                <motion.img
                  src={HERO_IMG}
                  alt="Solaris Transport European car transporter lorry delivering vehicles across UK motorways"
                  style={{ y: imgY, scale: 1.15 }}
                  className="h-80 w-full object-cover sm:h-[28rem]"
                  data-testid="hero-image"
                />
              </div>
              <div className="absolute -bottom-6 -left-4 rounded-2xl border border-slate-200 bg-white/90 px-5 py-4 shadow-xl backdrop-blur-md sm:-left-8" data-testid="hero-ontime-card">
                <p className="font-display text-2xl font-extrabold text-blue-700">99.8%</p>
                <p className="text-xs font-semibold text-slate-500">on-time completion</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ——— Editorial marquee ——— */}
      <div className="border-y border-slate-200 bg-slate-50 py-4" data-testid="coverage-marquee">
        <Marquee>
          {CITIES.map((c) => (
            <span key={c} className="mx-6 flex items-center gap-6 font-display text-sm font-bold tracking-[0.25em] text-slate-400">
              {c} <span className="h-1.5 w-1.5 rounded-full bg-blue-300" />
            </span>
          ))}
        </Marquee>
      </div>

      {/* ——— Stats band ——— */}
      <section className="bg-blue-700" data-testid="stats-section">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-y-10 px-5 py-14 lg:grid-cols-4 lg:px-8">
          {STATS.map((s, i) => (
            <FadeUp key={s.label} delay={i * 0.1} className="text-center">
              <p className="font-display text-4xl font-extrabold text-white sm:text-5xl" data-testid={`stat-value-${i}`}>
                <CountUp end={s.value} suffix={s.suffix} decimals={s.decimals || 0} />
              </p>
              <p className="mt-2 text-sm font-semibold text-blue-100">{s.label}</p>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* ——— Services bento ——— */}
      <section className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-28" data-testid="services-section">
        <FadeUp>
          <p className="text-xs font-semibold uppercase tracking-widest text-blue-700">What we move</p>
          <h2 className="mt-3 max-w-2xl font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
            Every kind of vehicle and equipment, on our own lorries
          </h2>
        </FadeUp>

        <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3" data-testid="services-grid">
          {SERVICES.map((s, i) => (
            <FadeUp key={s.title} delay={(i % 3) * 0.08} className={s.span ? "lg:col-span-2" : ""}>
              <div className="group flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white transition-all duration-300 hover:-translate-y-1 hover:border-blue-300 hover:shadow-xl hover:shadow-blue-900/5" data-testid={`service-card-${i}`}>
                {s.img && (
                  <div className="h-44 overflow-hidden">
                    <img src={s.img} alt={s.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                  </div>
                )}
                <div className="flex flex-1 flex-col p-6">
                  <div className="flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50">
                      <s.icon className="h-5 w-5 text-blue-700" />
                    </span>
                    <h3 className="font-display text-lg font-semibold tracking-tight text-slate-900">{s.title}</h3>
                  </div>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-600">{s.copy}</p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {s.tags.map((t) => (
                      <span key={t} className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">{t}</span>
                    ))}
                  </div>
                </div>
              </div>
            </FadeUp>
          ))}

          <FadeUp delay={0.16}>
            <Link to="/contact" data-testid="services-cta-card" className="group flex h-full flex-col justify-between rounded-2xl bg-slate-950 p-6 text-white transition-colors duration-300 hover:bg-blue-800">
              <div>
                <p className="font-display text-lg font-semibold">Something else to move?</p>
                <p className="mt-2 text-sm leading-relaxed text-slate-300 group-hover:text-blue-100">
                  If it fits on a lorry, we'll find a way. Speak to our operations team for a customised quote.
                </p>
              </div>
              <span className="mt-6 flex items-center gap-2 text-sm font-bold text-blue-300">
                Ask operations <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
              </span>
            </Link>
          </FadeUp>
        </div>
      </section>

      {/* ——— 3-step process ——— */}
      <section className="border-y border-slate-200 bg-white" data-testid="process-section">
        <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-28">
          <FadeUp>
            <p className="text-xs font-semibold uppercase tracking-widest text-blue-700">How it works</p>
            <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
              Three steps. Zero drama.
            </h2>
          </FadeUp>
          <div className="mt-12 grid gap-10 lg:grid-cols-3" data-testid="process-steps">
            {STEPS.map((s, i) => (
              <FadeUp key={s.n} delay={i * 0.12} className="relative">
                <p className="font-mono text-6xl font-semibold text-blue-100">{s.n}</p>
                <h3 className="mt-4 font-display text-xl font-semibold tracking-tight text-slate-900">{s.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">{s.copy}</p>
                {i < 2 && <div className="absolute right-0 top-8 hidden h-px w-16 bg-blue-200 lg:block" />}
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ——— Payment protection banner ——— */}
      <section className="mx-auto max-w-7xl px-5 py-20 lg:px-8" data-testid="payment-banner">
        <FadeUp>
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-blue-800 via-blue-700 to-blue-900 p-8 sm:p-12 lg:p-16">
            <PoundSterling className="pointer-events-none absolute -right-10 -top-10 h-64 w-64 text-white/5" strokeWidth={1} />
            <div className="relative max-w-2xl">
              <p className="text-xs font-semibold uppercase tracking-widest text-blue-200">Payment protection, as standard</p>
              <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
                Your money stays protected until the job is done
              </h2>
              <p className="mt-4 text-base leading-relaxed text-blue-100">
                Every payment is held by an independent, FCA-regulated payment
                service — never directly by us — and is only released when you
                confirm your vehicle has arrived safely.
              </p>
              <Link
                to="/payment-protection"
                data-testid="payment-banner-button"
                className="group mt-8 inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-base font-bold text-blue-800 transition-colors duration-200 hover:bg-blue-50"
              >
                See how it works
                <ArrowRight className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </FadeUp>
      </section>

      {/* ——— Testimonials ——— */}
      <section className="border-t border-slate-200 bg-slate-50" data-testid="testimonials-section">
        <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-28">
          <FadeUp className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-blue-700">Customer stories</p>
              <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
                Trusted on driveways, docks and depots
              </h2>
            </div>
            <div className="flex items-center gap-2" data-testid="testimonials-rating">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <span className="text-sm font-semibold text-slate-600">Rated excellent by our customers</span>
            </div>
          </FadeUp>

          <div className="mt-12 grid gap-5 lg:grid-cols-3" data-testid="testimonials-grid">
            {TESTIMONIALS.map((t, i) => (
              <FadeUp key={t.name} delay={i * 0.1}>
                <figure className="flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-7" data-testid={`testimonial-card-${i}`}>
                  <div className="flex gap-1">
                    {[...Array(5)].map((_, j) => (
                      <Star key={j} className="h-4 w-4 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <blockquote className="mt-4 flex-1 text-sm leading-relaxed text-slate-700">“{t.quote}”</blockquote>
                  <figcaption className="mt-6 border-t border-slate-100 pt-4">
                    <p className="font-display text-sm font-bold text-slate-900">{t.name}</p>
                    <p className="text-xs text-slate-500">{t.role}</p>
                  </figcaption>
                </figure>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ——— Final CTA ——— */}
      <section className="mx-auto max-w-7xl px-5 py-20 lg:px-8" data-testid="home-cta-section">
        <FadeUp className="flex flex-col items-start justify-between gap-8 rounded-3xl border border-slate-200 bg-white p-8 sm:p-12 lg:flex-row lg:items-center">
          <div>
            <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              Ready to move something?
            </h2>
            <p className="mt-3 max-w-xl text-base text-slate-600">
              Send us the collection and delivery postcodes and we'll come back
              with a fixed, customised quote — usually within one working day.
            </p>
          </div>
          <div className="flex flex-wrap gap-4">
            <Link to="/contact" data-testid="home-cta-quote-button" className="group flex items-center gap-2 rounded-full bg-blue-700 px-7 py-3.5 text-base font-bold text-white transition-colors duration-200 hover:bg-blue-800">
              Get a free quote <ArrowRight className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
            <a href="tel:+447462227041" data-testid="home-cta-call-button" className="flex items-center gap-2 rounded-full border border-slate-300 px-7 py-3.5 text-base font-bold text-slate-800 transition-colors duration-200 hover:border-blue-400 hover:text-blue-700">
              +44 7462 227041
            </a>
          </div>
        </FadeUp>
      </section>
    </div>
  );
};

export default Home;
